/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react'
import { Formik, Form, FormikProps } from 'formik'
import * as Analytics from 'core/common/analytics'
import { AnalyticsProductRegulatoryInformationField } from 'core/common/analytics/productFormEvents/types'
import i18n from 'core/common/i18n'
import CardStep from 'core/components/CardStep'
import { Button } from 'core/components/Core'
import { useFormContext } from 'core/pages/ProductNew-v2/context/ProductNewContext'
import { TProductInitialValues } from 'core/pages/ProductNew-v2/types'
import RegulatoryInformationFields from './RegulatoryInformationFields'
import { RegulatoryInformationFormSchema } from './schema'
import { RegulatoryInformationFormProps } from './types'

const fieldTranslations: Record<AnalyticsProductRegulatoryInformationField, string> = {
  mapa: 'mapa',
  anvisa: 'anvisa',
}

const allowedFields: AnalyticsProductRegulatoryInformationField[] = ['anvisa', 'mapa']

export const RegulatoryInformationForm = ({
  onNext,
  isEditMode,
  onEditSubmit,
  handleEditStep,
  step,
  formMode,
}: RegulatoryInformationFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [resetCheckbox, setResetCheckbox] = useState(false)
  const { formData, setFormData } = useFormContext()
  const initialValues = { anvisa: formData.anvisa, mapa: formData.mapa }

  useEffect(() => {
    const submitEdit = async () => {
      if (isSubmitting && isEditMode && onEditSubmit) {
        setIsSubmitting(false)
        setIsLoading(true)
        try {
          await onEditSubmit(formData)
          setIsLoading(false)
        } catch (error) {
          console.error('Error during API call', error)
        } finally {
          setIsLoading(false)
        }
      }
    }

    submitEdit()
  }, [isSubmitting, formData, isEditMode, onEditSubmit])

  const onSubmit = (values: typeof initialValues) => {
    Analytics.ProductRegulatoryInformationSaved(formMode)
    setFormData((prevData: TProductInitialValues) => ({
      ...prevData,
      anvisa: values.anvisa.trim(),
      mapa: values.mapa.trim(),
    }))

    setIsSubmitting(true)
    onNext()
  }

  const handleValidation = async ({ validateForm }: Pick<FormikProps<any>, 'validateForm'>) => {
    const errors = await validateForm()

    Object.keys(errors).forEach(field => {
      if (allowedFields.includes(field as AnalyticsProductRegulatoryInformationField)) {
        const translatedField =
          fieldTranslations[field as AnalyticsProductRegulatoryInformationField] || field

        Analytics.ProductRegulatoryInformationValidationError(
          formMode,
          translatedField as AnalyticsProductRegulatoryInformationField
        )
      }
    })
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={RegulatoryInformationFormSchema}
      enableReinitialize
    >
      {({ handleSubmit, dirty, resetForm, validateForm }) => {
        return (
          <Form className="w-100">
            <CardStep
              title={!isEditMode && i18n.t('productNew.regulatoryInformation.title')}
              description={i18n.t('productNew.regulatoryInformation.description')}
            >
              <RegulatoryInformationFields
                onChange={() => {
                  handleEditStep(step.key)
                  setResetCheckbox(false)
                }}
                resetFields={resetCheckbox}
              />

              <div className="w-100 d-flex justify-end">
                {!isEditMode && (
                  <>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => {
                        resetForm()
                        handleEditStep(null)
                        setResetCheckbox(true)
                      }}
                      className="mr-2"
                    >
                      <span>Cancelar</span>
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        handleSubmit()
                        handleValidation({ validateForm })
                      }}
                    >
                      {i18n.t('productNew.actions.continue')}
                    </Button>
                  </>
                )}

                {isEditMode && dirty && (
                  <>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => resetForm()}
                      className="mr-2"
                    >
                      <span>Cancelar</span>
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        handleSubmit()
                        handleValidation({ validateForm })
                      }}
                      loading={isLoading}
                    >
                      <span>Salvar</span>
                    </Button>
                  </>
                )}
              </div>
            </CardStep>
          </Form>
        )
      }}
    </Formik>
  )
}
